import { useMemo } from 'react';
import { CheckCircle2, AlertCircle, ChevronRight } from 'lucide-react';
import { Progress } from '../components/ui/progress';

const ProfileCompletionIndicator = ({ profile, userRole, onEditField }) => {
  const completionData = useMemo(() => {
    if (!profile) return { percentage: 0, complete: [], incomplete: [] };

    // Different fields for facilitators vs camps
    const facilitatorFields = [
      { 
        key: 'profile_photo', 
        label: 'Profile photo', 
        check: () => !!profile.profile_photo,
        weight: 10,
        section: 'profile'
      },
      { 
        key: 'bio', 
        label: 'Bio (min 50 characters)', 
        check: () => profile.bio && profile.bio.length >= 50,
        weight: 10,
        section: 'profile'
      },
      { 
        key: 'years_experience', 
        label: 'Years of experience', 
        check: () => profile.years_experience > 0,
        weight: 10,
        section: 'profile'
      },
      { 
        key: 'first_aid', 
        label: 'First aid status', 
        check: () => profile.first_aid_certified !== undefined,
        weight: 10,
        section: 'profile'
      },
      { 
        key: 'provinces', 
        label: 'Provinces available', 
        check: () => profile.provinces && profile.provinces.length > 0,
        weight: 10,
        section: 'profile'
      },
      { 
        key: 'age_groups', 
        label: 'Age groups', 
        check: () => profile.age_groups && profile.age_groups.length > 0,
        weight: 10,
        section: 'profile'
      },
      { 
        key: 'camp_types', 
        label: 'Camp types', 
        check: () => profile.camp_types && profile.camp_types.length > 0,
        weight: 10,
        section: 'profile'
      },
      { 
        key: 'specialties', 
        label: 'Specialties', 
        check: () => profile.specialties && profile.specialties.length > 0,
        weight: 10,
        section: 'profile'
      },
      { 
        key: 'contact', 
        label: 'Contact method', 
        check: () => profile.contact_email || profile.contact_phone,
        weight: 10,
        section: 'profile'
      },
      { 
        key: 'documents', 
        label: 'Qualification document', 
        check: () => profile.documents && profile.documents.length > 0,
        weight: 10,
        section: 'profile'
      },
      { 
        key: 'references', 
        label: 'Two professional references', 
        check: () => {
          const refs = (profile.references || []).filter(r =>
            ['full_name','role','organisation','contact_number','relationship'].every(f => String(r[f] || '').trim())
          );
          return refs.length >= 2;
        },
        weight: 10,
        section: 'profile'
      },
    ];

    const campFields = [
      { 
        key: 'cover_image', 
        label: 'Cover image', 
        check: () => !!profile.cover_image,
        weight: 15,
        section: 'profile'
      },
      { 
        key: 'description', 
        label: 'Description (min 50 characters)', 
        check: () => profile.description && profile.description.length >= 50,
        weight: 15,
        section: 'profile'
      },
      { 
        key: 'location', 
        label: 'Location details', 
        check: () => profile.province && profile.nearest_town,
        weight: 15,
        section: 'profile'
      },
      { 
        key: 'age_range', 
        label: 'Age range', 
        check: () => profile.age_range_min !== undefined && profile.age_range_max !== undefined,
        weight: 10,
        section: 'profile'
      },
      { 
        key: 'capacity', 
        label: 'Capacity', 
        check: () => profile.capacity > 0,
        weight: 10,
        section: 'profile'
      },
      { 
        key: 'activities', 
        label: 'Activities offered', 
        check: () => profile.activities && profile.activities.length > 0,
        weight: 10,
        section: 'profile'
      },
      { 
        key: 'contact', 
        label: 'Contact information', 
        check: () => profile.contact_email || profile.contact_phone,
        weight: 15,
        section: 'profile'
      },
      { 
        key: 'website', 
        label: 'Website', 
        check: () => !!profile.website,
        weight: 10,
        section: 'profile'
      },
    ];

    const fields = userRole === 'camp' ? campFields : facilitatorFields;

    const complete = [];
    const incomplete = [];
    let totalWeight = 0;
    let maxWeight = 0;

    fields.forEach(field => {
      maxWeight += field.weight;
      if (field.check()) {
        complete.push(field);
        totalWeight += field.weight;
      } else {
        incomplete.push(field);
      }
    });

    const percentage = maxWeight > 0 ? Math.round((totalWeight / maxWeight) * 100) : 0;

    return {
      percentage,
      complete,
      incomplete
    };
  }, [profile, userRole]);

  if (!profile) return null;

  const isComplete = completionData.percentage === 100;

  const handleItemClick = (fieldKey) => {
    if (onEditField) {
      onEditField(fieldKey);
    } else {
      // Scroll to the Profile tab content area
      const profileSection = document.querySelector('[data-testid="profile-tab-content"]');
      if (profileSection) {
        profileSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  };

  return (
    <div className={`rounded-2xl border p-6 mb-6 ${
      isComplete 
        ? 'bg-primary/5 border-primary/20' 
        : 'bg-card border-border'
    }`} data-testid="profile-completion-indicator">
      {isComplete ? (
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
            <CheckCircle2 className="w-6 h-6 text-primary" />
          </div>
          <div>
            <h3 className="font-heading font-semibold text-lg text-primary">Profile Complete!</h3>
            <p className="text-sm text-muted-foreground">
              Your profile is ready to be discovered. Share your profile link to get started.
            </p>
          </div>
        </div>
      ) : (
        <>
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading font-semibold text-lg">
              Profile {completionData.percentage}% complete
            </h3>
            <span className="text-sm text-muted-foreground">
              {completionData.incomplete.length} items remaining
            </span>
          </div>
          
          <Progress value={completionData.percentage} className="h-3 mb-4" />
          
          <div className="space-y-2">
            <p className="text-sm font-medium text-muted-foreground mb-2">
              Complete these to improve your profile:
            </p>
            {completionData.incomplete.map((field) => (
              <button
                key={field.key}
                onClick={() => handleItemClick(field.key)}
                className="flex items-center justify-between w-full p-3 bg-muted/50 hover:bg-muted rounded-xl transition-colors text-left group cursor-pointer"
                data-testid={`completion-item-${field.key}`}
              >
                <div className="flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 text-campfire" />
                  <span className="text-sm">{field.label}</span>
                </div>
                <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-foreground transition-colors" />
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default ProfileCompletionIndicator;
